import { reactive, ref, computed } from "vue";
import { useStore } from "vuex";
import type { ShopInput, ShopCountInput, CreateShopMutation } from "@/generated/graphql";
import {
  localStorageGetAuthType,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { DeliverableDisplay, GeneralTypeList } from "@/common/constant";
import { checkChainListDisplay } from "@/common/business";
import type {
  ChainItem,
  ChainSelectValue,
  DssConnectionItem,
  DssSelectValue,
  DeliverableSelectValue,
  IndexItemSelectValue,
  HolidaySelectValue,
} from "../types";
import { REQUIRED_FIELD_LABEL_KEYS } from "../constants";

/** 店舗登録画面のフォーム状態を扱う composable。 */
export const useShopRegisterForm = () => {
  // --- Store ---
  const store = useStore();
  // 店舗一覧編集関係
  const listEditFlag = computed<boolean>(() => store.getters.listEditFlag);

  // -----------------------------------------------------------------------------
  // 店舗作成 input
  // -----------------------------------------------------------------------------
  /**
   * 店舗作成 input
   */
  const shopCreateInput = reactive<ShopInput>({
    address1: "",
    address2: "",
    address3: "",
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    dss_connection_id: 0,
    dss_shop_code: "",
    point_server_store_code: "",
    point_server_ip_address: "",
    point_server_db_name: "",
    fax: "",
    delivery_request_notification_emails: "",
    name: "",
    name_kana: "",
    phone: "",
    shop_code: "",
    tmp_member_registration_flg: 0,
    zip_code: "",
    user_id: localStorageGetUserId(),
    alert_lower_threshold: DeliverableDisplay.triangle_min_value,
    alert_upper_threshold: DeliverableDisplay.triangle_max_value,
    shipping_label_print_flg: 0,
    delivery_order_print_flg: 0,
    double_deli_oricon_count: null,
    charter_deli_oricon_count: null,
    holiday_fri: 0,
    holiday_mon: 0,
    holiday_sat: 0,
    holiday_sun: 0,
    holiday_thu: 0,
    holiday_tue: 0,
    holiday_wed: 0,
    lot_code: "",
  });

  const getShopCountCountInput = reactive<ShopCountInput>({
    address: "",
    chain_code: "",
    customer_code: "",
    phone: "",
    shop_list: [],
    status_configured: null,
    zip: "",
  });

  // -----------------------------------------------------------------------------
  // 選択肢・選択値
  // -----------------------------------------------------------------------------
  //選択された値を格納する変数
  const deliverableItems = GeneralTypeList.display_method;
  const defaultDeliverable =
    deliverableItems.find(
      (element) => element.index === DeliverableDisplay.symbol,
    ) ?? deliverableItems[0] ?? { index: "", item: "" };
  const deliverableSelectValues = ref<DeliverableSelectValue>({
    index: defaultDeliverable.index,
    item: defaultDeliverable.item,
  });

  const shippingLabelPrintItems = GeneralTypeList.displashipping_label_print;
  const deliveryOrderPrintItems = GeneralTypeList.display_delivery_order_print;
  const tmpMemberRegistrationItems =
    GeneralTypeList.display_tmp_member_registration_flg;
  const defaultTmpMemberRegistration =
    tmpMemberRegistrationItems.find((element) => element.index === 0) ??
    tmpMemberRegistrationItems[0] ??
    ({ index: 0, item: "利用しない" } as { index: number; item: string });

  const shippingLabelPrintSelectValues = ref<IndexItemSelectValue>({
    index: 0,
    item: "印刷しない",
  });
  const deliveryOrderPrintSelectValues = ref<IndexItemSelectValue>({
    index: 0,
    item: "印刷しない",
  });
  const tmpMemberRegistrationSelectValues = ref<IndexItemSelectValue>({
    index: defaultTmpMemberRegistration.index,
    item: defaultTmpMemberRegistration.item,
  });

  /**
   * 休日設定の選択ボックス
   */
  const holidayItems = GeneralTypeList.holiday;
  const holidaySelectValues = ref<HolidaySelectValue[]>([]);

  // -----------------------------------------------------------------------------
  // チェーン
  // -----------------------------------------------------------------------------
  //ユーザ権限取得
  const chainViewFlag = ref<boolean>(
    checkChainListDisplay(localStorageGetAuthType()),
  );

  /**
   * チェーンリスト
   */
  const chainListItems = ref<ChainItem[]>([]);
  const chainSelectValues = ref<ChainSelectValue>({
    chain_code: "",
    name: "",
  });

  // チェーンコードが空なら、選択値またはローカルストレージから補完する
  const ensureChainCode = (): string => {
    if (shopCreateInput.chain_code) return shopCreateInput.chain_code;
    if (chainSelectValues.value?.chain_code) {
      shopCreateInput.chain_code = chainSelectValues.value.chain_code;
      return shopCreateInput.chain_code;
    }
    const stored = localStorageGetChainCode();
    if (stored) shopCreateInput.chain_code = stored;
    return shopCreateInput.chain_code;
  };

  // -----------------------------------------------------------------------------
  // DSS
  // -----------------------------------------------------------------------------
  /**
   * DSS接続先
   */
  const dssListItems = ref<DssConnectionItem[]>([]);
  const dssSelectValues = ref<DssSelectValue>({
    connection_id: 0,
    connection_name: "連携しない",
  });
  const dss_shop_name = ref<string>("");

  // -----------------------------------------------------------------------------
  // その他
  // -----------------------------------------------------------------------------
  const isDeliverableNumberDisplay = ref<boolean>(false);
  // レシートバーコード検索を使用するかどうかのフラグ
  const useReceiptBarcodeSearch = ref<boolean>(false);
  const formRef = ref<{ setFieldValue: (field: string, value: unknown) => void } | null>(null);
  // 店舗作成 レスポンス（元の class フィールド）
  const createShopMutation = ref<CreateShopMutation | null | undefined>(null);

  // -----------------------------------------------------------------------------
  // 文言
  // -----------------------------------------------------------------------------
  const wordList = ref<Record<string, string>>({});
  const deliveryRequestwordList = ref<Record<string, string>>({});

  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return REQUIRED_FIELD_LABEL_KEYS.every((key) => key in wordList.value);
  });

  /**
   * ２件分配達の基準個口数のツールチップメッセージ取得＆置換
   */
  const doubleDeliOriconCountHintMessage = computed<string>(() => {
    const msg = wordList.value.msg_i_double_deli_oricon_count;
    const deliMsg = deliveryRequestwordList.value.lbl_double_entry_chk;

    if (msg && deliMsg) {
      return msg.replace("{{deli_msg}}", deliMsg.replace("{{n}}", "N"));
    } else {
      return ""; // または適切なデフォルトメッセージ
    }
  });

  /**
   * チャーター便の基準個口数のツールチップメッセージ取得＆置換
   */
  const charterDeliOriconCountHintMessage = computed<string>(() => {
    const msg = wordList.value.msg_i_lbl_charter_deli_oricon_count;
    const deliMsg = deliveryRequestwordList.value.lbl_charter_chk;

    if (msg && deliMsg) {
      return msg.replace("{{deli_msg}}", deliMsg.replace("{{n}}", "N"));
    } else {
      return ""; // または適切なデフォルトメッセージ
    }
  });

  // 編集画面初期表示時に vee-validate の内部値を同期して validate() が通るようにする。
  const syncFormValues = (): void => {
    if (!formRef.value || !isWordListReady.value) return;
    const form = formRef.value;
    form.setFieldValue(
      wordList.value.lbl_chain_name,
      chainSelectValues.value ?? {
        chain_code: shopCreateInput.chain_code,
        name: "",
      },
    );
    form.setFieldValue(wordList.value.lbl_shop_code, shopCreateInput.shop_code);
    form.setFieldValue(wordList.value.lbl_shop_name, shopCreateInput.name);
    form.setFieldValue(wordList.value.lbl_shop_name_kana, shopCreateInput.name_kana);
    form.setFieldValue(wordList.value.lbl_dss_connection_id, dssSelectValues.value);
    form.setFieldValue(wordList.value.lbl_dss_shop_code, shopCreateInput.dss_shop_code);
    form.setFieldValue("zip_code", shopCreateInput.zip_code);
    form.setFieldValue(wordList.value.lbl_address_detail, shopCreateInput.address2);
    form.setFieldValue(wordList.value.lbl_room_num, shopCreateInput.address3);
    form.setFieldValue(wordList.value.lbl_tel, shopCreateInput.phone);
    form.setFieldValue(wordList.value.lbl_fax, shopCreateInput.fax);
    form.setFieldValue(
      wordList.value.lbl_cur_deliverable_display_method,
      deliverableSelectValues.value,
    );
    form.setFieldValue(
      wordList.value.lbl_alert_upper_threshold,
      shopCreateInput.alert_upper_threshold,
    );
    form.setFieldValue(
      wordList.value.lbl_shipping_label_print_flg,
      shippingLabelPrintSelectValues.value,
    );
    form.setFieldValue(
      wordList.value.lbl_delivery_order_print_flg,
      deliveryOrderPrintSelectValues.value,
    );
    form.setFieldValue(
      wordList.value.lbl_delivery_request_notification_emails,
      shopCreateInput.delivery_request_notification_emails,
    );
    form.setFieldValue(
      wordList.value.lbl_double_deli_oricon_count,
      shopCreateInput.double_deli_oricon_count,
    );
    form.setFieldValue(
      wordList.value.lbl_charter_deli_oricon_count,
      shopCreateInput.charter_deli_oricon_count,
    );
    form.setFieldValue(
      wordList.value.lbl_temp_member_registration_flg,
      tmpMemberRegistrationSelectValues.value,
    );
  };

  /**
   * 配達残数の表示方法選択時 @change
   */
  const onChangeDeliverable = async (index: string): Promise<void> => {
    if (index === DeliverableDisplay.number) {
      isDeliverableNumberDisplay.value = true;
      shopCreateInput.alert_lower_threshold = undefined;
      shopCreateInput.alert_upper_threshold = undefined;
    } else {
      isDeliverableNumberDisplay.value = false;
      shopCreateInput.alert_lower_threshold =
        DeliverableDisplay.triangle_min_value;
      shopCreateInput.alert_upper_threshold =
        DeliverableDisplay.triangle_max_value;
    }
  };

  /**
   * 編集モードの場合に休日を設定する
   */
  const updateModeSetHoliday = async (): Promise<void> => {
    holidaySelectValues.value = [];
    if (shopCreateInput.holiday_mon !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 1)!,
      );
    if (shopCreateInput.holiday_tue !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 2)!,
      );
    if (shopCreateInput.holiday_wed !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 3)!,
      );
    if (shopCreateInput.holiday_thu !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 4)!,
      );
    if (shopCreateInput.holiday_fri !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 5)!,
      );
    if (shopCreateInput.holiday_sat !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 6)!,
      );
    if (shopCreateInput.holiday_sun !== 0)
      holidaySelectValues.value.push(
        GeneralTypeList.holiday.find((e) => e.value === 7)!,
      );
  };

  /**
   * 編集モードの場合に配達残数の設定をする
   */
  const updateModeSetDelivarable = async (): Promise<void> => {
    if (shopCreateInput.alert_lower_threshold) {
      isDeliverableNumberDisplay.value = false;
      const found = GeneralTypeList.display_method.find(
        (element) => element.index === DeliverableDisplay.symbol,
      )!;
      deliverableSelectValues.value = { index: found.index, item: found.item };
    } else {
      isDeliverableNumberDisplay.value = true;
      const found = GeneralTypeList.display_method.find(
        (element) => element.index === DeliverableDisplay.number,
      )!;
      deliverableSelectValues.value = { index: found.index, item: found.item };
    }
    onChangeDeliverable(deliverableSelectValues.value.index);
  };

  /**
   * 入力項目をクリアする
   */
  const allClear = async (): Promise<void> => {
    shopCreateInput.address1 = "";
    shopCreateInput.address2 = "";
    shopCreateInput.address3 = "";
    shopCreateInput.chain_code = "";
    shopCreateInput.customer_code = localStorageGetCustomerCode();
    shopCreateInput.dss_connection_id = 0;
    shopCreateInput.dss_shop_code = "";
    shopCreateInput.fax = "";
    shopCreateInput.point_server_store_code = "";
    shopCreateInput.point_server_ip_address = "";
    shopCreateInput.point_server_db_name = "";
    shopCreateInput.delivery_request_notification_emails = "";
    shopCreateInput.name = "";
    shopCreateInput.name_kana = "";
    shopCreateInput.phone = "";
    shopCreateInput.shop_code = "";
    shopCreateInput.zip_code = "";
    shopCreateInput.user_id = localStorageGetUserId();
    shopCreateInput.alert_lower_threshold = DeliverableDisplay.triangle_min_value;
    shopCreateInput.alert_upper_threshold = DeliverableDisplay.triangle_max_value;
    shopCreateInput.holiday_mon = 0;
    shopCreateInput.holiday_tue = 0;
    shopCreateInput.holiday_wed = 0;
    shopCreateInput.holiday_thu = 0;
    shopCreateInput.holiday_fri = 0;
    shopCreateInput.holiday_sat = 0;
    shopCreateInput.holiday_sun = 0;
    shopCreateInput.shipping_label_print_flg = 0;
    // ラジオ選択値（荷札印刷）
    shippingLabelPrintSelectValues.value = shippingLabelPrintItems.find(
      (element: { index: number }) => element.index === 0,
    )!;
    shopCreateInput.delivery_order_print_flg = 0;
    // ラジオ選択値（配達伝票印刷）
    deliveryOrderPrintSelectValues.value = deliveryOrderPrintItems.find(
      (element: { index: number }) => element.index === 0,
    )!;
    shopCreateInput.tmp_member_registration_flg = 0;
    // ラジオ選択値（仮会員登録機能）
    const tmpFound = tmpMemberRegistrationItems.find(
      (element: { index: number }) => element.index === 0,
    );
    if (tmpFound) tmpMemberRegistrationSelectValues.value = tmpFound;
    shopCreateInput.double_deli_oricon_count = null;
    shopCreateInput.charter_deli_oricon_count = null;

    // 休日選択
    holidaySelectValues.value = [];
    // 残配達可能数の表示方法
    deliverableSelectValues.value = {
      index: defaultDeliverable.index,
      item: defaultDeliverable.item,
    };
    isDeliverableNumberDisplay.value = false;

    // チェーン選択（null を代入して v-autocomplete の選択状態を完全にリセット）
    chainSelectValues.value = null;

    // DSS 関連
    dssListItems.value = [];
    Object.assign(dssSelectValues.value!, {
      connection_id: 0,
      connection_name: "連携しない",
    });
    dss_shop_name.value = "";

    // ユーザ権限取得
    chainViewFlag.value = checkChainListDisplay(localStorageGetAuthType());

    // チェーンコードの再設定（初期表示時と同じ状態に復元する）
    if (!chainViewFlag.value) {
      // 本部権限: ログインユーザのチェーンコードを設定
      chainSelectValues.value = {
        chain_code: localStorageGetChainCode(),
        name: "",
      };
      shopCreateInput.chain_code = localStorageGetChainCode();
    } else {
      // 運営管理者: チェーンコードをクリア（ドロップダウンから選択する仕様）
      shopCreateInput.chain_code = "";
    }
  };

  return {
    shopCreateInput,
    getShopCountCountInput,
    formRef,
    chainListItems,
    chainSelectValues,
    chainViewFlag,
    ensureChainCode,
    dssListItems,
    dssSelectValues,
    dss_shop_name,
    deliverableItems,
    defaultDeliverable,
    deliverableSelectValues,
    isDeliverableNumberDisplay,
    useReceiptBarcodeSearch,
    shippingLabelPrintItems,
    shippingLabelPrintSelectValues,
    deliveryOrderPrintItems,
    deliveryOrderPrintSelectValues,
    tmpMemberRegistrationItems,
    tmpMemberRegistrationSelectValues,
    holidayItems,
    holidaySelectValues,
    wordList,
    deliveryRequestwordList,
    isWordListReady,
    doubleDeliOriconCountHintMessage,
    charterDeliOriconCountHintMessage,
    createShopMutation,
    listEditFlag,
    syncFormValues,
    allClear,
    onChangeDeliverable,
    updateModeSetHoliday,
    updateModeSetDelivarable,
  };
};
